import { Component, OnInit, ViewChild, DestroyRef, inject } from '@angular/core';
import { NgForm, FormsModule } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { timer } from 'rxjs';
import { LOGO } from '../../../core/models/logo.enum';
import { LoginService } from '../../../core/services/login.service';
import { SessionStorageService } from '../../../core/services/session-storage.service';
import { OtpForm, ResetForm } from '../../models/reset-password/ResetForm';
import { AuthRoutes } from '../../models/routes/AuthRoutes.constant';
import { LoginChallengeDataService } from '../../../core/services/login-challenge-data.service';
import { finalize } from 'rxjs/operators';
import { GtAutofocusDirective } from '../../../core/directives/gt-autofocus.directive';
import { NgIf, NgClass } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-forgot-password',
    templateUrl: 'forgot-password.component.html',
    styleUrls: ['forgot-password.component.scss'],
    imports: [NgIf, FormsModule, GtAutofocusDirective, NgClass]
})
export class ForgotPasswordComponent implements OnInit {
  public resetForm: ResetForm;
  public resetMessage = null;
  public resetError = "";
  public loginChallenge = '';
  public domainName = '';
  public isSubmbitting = false;
  public clientLogo = LOGO.CLIENT;
  public readonly emoji = '&#128522;';
  public otpForm: OtpForm;
  public countdown: number = 30;
  public countdownFormattedValue: string = '';
  public showCountDown: boolean = true;
  public resetSuccessResponse: any;
  public displayDefaultTemplate: boolean = false;
  public isAlumni: boolean = false;
  public hasMobileNumber: boolean = false;
  public invalidOtp: boolean = false;
  public oAuthPath = AuthRoutes;
  // TODO: Fetch from email from backend
  public userEmailId = 'example@gmail.com';

  private isOptSubmitting: boolean = false;
  private countdownSubscription$: any;
  @ViewChild('forgotPasswordForm')
  private formGroup: NgForm;
  @ViewChild('mobileOtpForm')
  private otpFormGroup: NgForm;
  private destroyRef = inject(DestroyRef);

  constructor(private loginService: LoginService,
    private _sessionStorage: SessionStorageService,
    private _router: Router,
    private _route: ActivatedRoute,
    private loginChallengeDataService: LoginChallengeDataService) {
    this.resetErrorSubscriber = this.resetErrorSubscriber.bind(this);
    this.resetSuccessSubscriber = this.resetSuccessSubscriber.bind(this);
  }

  public ngOnInit() {
    this.otpForm = <OtpForm>{};
    this.resetForm = <ResetForm>{};
    this.loginChallenge = this.loginChallengeDataService.getLoginChallenge();
    this._route.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(this.loadRouteParams.bind(this));
    this.loginChallengeDataService.loginChallengeSource
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(this.onLoginCallengeChange.bind(this));
  }

  public onSubmit(resetByMobile: boolean) {
    if (this.formGroup?.invalid || this.isSubmbitting) {
      this.resetError = "Invalid Login ID";
      return;
    }
    this.isSubmbitting = true;
    this.resetForm.resetByMobile = resetByMobile;
    this.loginService.sendResetLink(this.resetForm, this.loginChallenge)
      .pipe(
        finalize(() => this.isSubmbitting = false),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(this.resetSuccessSubscriber, this.resetErrorSubscriber);
  }

  public goToLogin() {
    this._router.navigate([AuthRoutes.LOGIN], { queryParams: { login_challenge: this.loginChallenge } });
  }

  public onOtpSubmit() {
    if (this.otpFormGroup?.invalid || this.isOptSubmitting) {
      return;
    }
    this.isOptSubmitting = true;
    let data = {
      loginId: this.resetSuccessResponse.loginId,
      otp: String(this.otpForm.otp)
    }
    this.loginService.verifyOtp(data, this.loginChallenge)
      .pipe(
        finalize(() => this.isOptSubmitting = false),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((result) => {
        if(result?.otpVerified) {
          this._router.navigate([AuthRoutes.RESET_PASSWORD], {queryParams: {domain: result?.domain, guid: result?.guid}})
        } else {
          this.invalidOtp = true;
        }
      }, (error) => this.invalidOtp = true);
  }

  public maskTheEmail(email: string) {
    if (!email) return '';
    const indexOfAt = email.indexOf('@');
    if (indexOfAt === -1) return email;
    
    //For displaying the first 3 characters
    const prefix = indexOfAt <= 4 ? '' : email.substring(0, 3);
    //For masking the middle characters
    const maskedPart = '*'.repeat(indexOfAt <= 4 ? indexOfAt : indexOfAt - 4);
    //For displaying the last 1 characters with the domain
    const domainPart = email.substring(indexOfAt - (indexOfAt <= 4 ? 0 : 1));

    return prefix + maskedPart + domainPart;
  }

  public maskMobileNumber(mobile: number) {
    if (!mobile) return '';

    let mobileNumber = String(mobile)
    const maskedPart = '*'.repeat(String(mobileNumber).length - 3);
    const visiblePrefix = mobileNumber.substring(0, 1);
    const visibleSuffix = mobileNumber.substring(mobileNumber.length - 2);

    return visiblePrefix + maskedPart + visibleSuffix;
  }

  public onCantAccessEmail(): void {
    this.displayDefaultTemplate = this.resetSuccessResponse.alternateMobileNo ? false : true;
    this.hasMobileNumber = !this.displayDefaultTemplate;
    this.resetSuccessResponse.alternateMobileNo && this.startCountdown();
    this.onSubmit(true); // resetByMobile is true
  }

  public onResendOtp() {
    this.startCountdown();
    this.showCountDown = true;
    this.onSubmit(true); // resetByMobile is true
  }

  public startCountdown() {
    this.countdown = 30;
    const source = timer(0, 1000);
    this.countdownSubscription$ = source
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.countdown--;
        this.countdownFormattedValue = this.formatCountdown(this.countdown);
        if (this.countdown === 0) {
          this.showCountDown = false;
          this.countdown = 30;
        }
      });
  }

  public formatCountdown(value): string {
    const minutes = Math.floor(value / 60);
    const seconds = value % 60;
    return `${this.formatTime(minutes)}:${this.formatTime(seconds)}`;
  }

  private formatTime(time: number): string {
    return time < 10 ? `0${time}` : time.toString();
  }


  private loadRouteParams(params: Params) {
    this.domainName = params.domain;
  }

  private resetSuccessSubscriber(res) {
    this.resetSuccessResponse = res;
    this.resetMessage = res?.message;
    this.isAlumni = res?.exitedEmployee;
    !(this.resetSuccessResponse.alternateEmail && this.resetSuccessResponse.alternateMobileNo) && this.startCountdown();
  }
  private resetErrorSubscriber({ error }) {
    this.resetError = error?.message || 'Invalid Login ID';
  }

  private onLoginCallengeChange(value) {
    this.loginChallenge = value;
  }
}
